import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { StepPackage } from "../components/booking/StepPackage";
import { StepEvent, StepClient } from "../components/booking/StepFields";
import { StepPayment } from "../components/booking/StepPayment";
import { StepContract } from "../components/booking/StepContract";
import { Summary } from "../components/booking/Summary";
import { PACKAGES, OTHER_CITY } from "../lib/data";
import { daysUntil, isShortNotice } from "../lib/pricing";
import { api, apiError } from "../lib/api";

const TITLES = ["Pacote", "Evento", "Seus dados", "Pagamento", "Contrato"];

const validate = (step, f, unavailable = []) => {
  if (step === 1) {
    const d = daysUntil(f.eventDate);
    if (!f.eventDate || d === null || d < 1) return "Escolha uma data futura para o evento.";
    if (unavailable.includes(f.eventDate)) return "Esta data já está reservada. Escolha outra data.";
    if (!f.eventTime) return "Informe o horário de início.";
    if (isShortNotice(f.eventDate, f.eventTime)) return "Para eventos com menos de 48 horas de antecedência, confirme a disponibilidade pelo WhatsApp.";
    if (!f.city) return "Selecione a cidade.";
    if (f.city === OTHER_CITY) return "Para outras cidades, consulte o deslocamento pelo WhatsApp.";
    if (f.address.trim().length < 5) return "Informe o endereço do evento.";
    if (f.venueName.trim().length < 2) return "Informe o nome do local do evento.";
    if (!(parseInt(f.guests, 10) >= 1)) return "Informe o número estimado de convidados.";
    if (f.accessDetails.trim().length < 5) return "Descreva as condições de acesso ao local (pavimento, escadas, elevador...).";
  }
  if (step === 2) {
    if (f.name.trim().length < 3) return "Informe seu nome completo.";
    if (f.document.replace(/\D/g, "").length < 11) return "Informe um CPF ou CNPJ válido.";
    if (f.phone.replace(/\D/g, "").length < 10) return "Informe um WhatsApp válido.";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email)) return "Informe um e-mail válido.";
  }
  return null;
};

export default function Booking() {
  const [params] = useSearchParams();
  const initial = PACKAGES.some((p) => p.id === params.get("pacote")) ? params.get("pacote") : "pacote-2";
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ packageId: initial, extras: 0, eventDate: "", eventTime: "", city: "", address: "", venueName: "", guests: "", accessDetails: "", name: "", document: "", phone: "", email: "", notes: "", option: "full", method: "stripe" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const [unavailable, setUnavailable] = useState([]);
  const [pagarmeEnabled, setPagarmeEnabled] = useState(false);
  const [contract, setContract] = useState(null);
  const [contractLoading, setContractLoading] = useState(false);
  const [acceptAccess, setAcceptAccess] = useState(false);
  const [acceptContract, setAcceptContract] = useState(false);

  const payload = () => ({
    package_id: form.packageId, extra_plushies: form.extras, payment_option: form.option,
    payment_method: pagarmeEnabled ? form.method : "stripe",
    event_date: form.eventDate, event_time: form.eventTime, city: form.city, address: form.address,
    venue_name: form.venueName, guests: parseInt(form.guests, 10) || 0, access_details: form.accessDetails,
    client_name: form.name, client_document: form.document, client_phone: form.phone,
    client_email: form.email, notes: form.notes, origin_url: window.location.origin,
  });

  const openContract = async () => {
    setContract(null);
    setAcceptAccess(false);
    setAcceptContract(false);
    setContractLoading(true);
    setStep(4);
    try {
      const { data } = await api.post("/contracts/preview", payload());
      setContract(data ?? null);
    } catch (e) {
      toast.error(apiError(e, "Não foi possível gerar o contrato."));
      setStep(3);
    } finally {
      setContractLoading(false);
    }
  };

  useEffect(() => {
    api.get("/config").then(({ data }) => setPagarmeEnabled(!!data?.pagarme_enabled)).catch(() => setPagarmeEnabled(false));
  }, []);

  const loadAvailability = useCallback(async () => {
    try {
      const { data } = await api.get("/availability");
      setUnavailable(Array.isArray(data?.unavailable) ? data.unavailable : []);
    } catch (e) { /* calendar still works; server re-checks */ }
  }, []);

  useEffect(() => { loadAvailability(); }, [loadAvailability]);

  const next = () => {
    const err = validate(step, form, unavailable);
    if (err) return toast.error(err);
    if (step === 1 && daysUntil(form.eventDate) < 10) set("option", "full");
    if (step === 3) return openContract();
    setStep((s) => Math.min(3, s + 1));
  };

  const pay = async () => {
    if (!(acceptAccess && acceptContract && contract?.hash)) return;
    setLoading(true);
    try {
      const { data } = await api.post("/bookings/checkout", {
        ...payload(), contract_hash: contract.hash, accept_access: acceptAccess, accept_contract: acceptContract,
      });
      if (data?.checkout_url) window.location.href = data.checkout_url;
      else throw new Error("no url");
    } catch (e) {
      toast.error(apiError(e, "Não foi possível iniciar o pagamento."));
      if (e?.response?.status === 409) {
        if (String(e?.response?.data?.detail || "").includes("contrato")) {
          setLoading(false);
          return openContract();
        }
        await loadAvailability();
        set("eventDate", "");
        setStep(1);
      }
      setLoading(false);
    }
  };

  const Steps = [StepPackage, StepEvent, StepClient, StepPayment, StepContract];
  const Current = Steps[step];
  const canPay = acceptAccess && acceptContract && !!contract?.hash && !loading;

  return (
    <main data-testid="booking-page" className="relative pt-[110px] pb-24 min-h-screen">
      <div className="absolute inset-x-0 top-0 h-[420px] bg-[radial-gradient(ellipse_60%_80%_at_20%_0%,rgba(255,210,0,0.10),transparent)]" />
      <div className="relative max-w-[1300px] mx-auto px-5 lg:px-10">
        <p className="font-mono text-xs uppercase tracking-[0.28em] text-[#FFD200]">Fechamento de reserva</p>
        <h1 className="mt-4 font-display font-black uppercase text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">Garanta sua <span className="text-gold-grad">data.</span></h1>
        <div className="mt-10 grid grid-cols-5 gap-2" data-testid="booking-stepper">
          {TITLES.map((t, i) => (
            <button key={t} type="button" onClick={() => i < step && setStep(i)} data-testid={`stepper-${i + 1}`} className="text-left">
              <div className="h-1 rounded-full bg-white/10 overflow-hidden">
                <motion.div className="h-full bg-[#FFD200]" initial={false} animate={{ width: i <= step ? "100%" : "0%" }} transition={{ duration: 0.6 }} />
              </div>
              <p className={`mt-3 font-mono text-[11px] uppercase tracking-[0.15em] ${i <= step ? "text-white" : "text-zinc-500"}`}>0{i + 1} <span className="hidden sm:inline">· {t}</span></p>
            </button>
          ))}
        </div>
        <div className="mt-10 grid lg:grid-cols-12 gap-10">
          <section className="lg:col-span-7">
            <h2 className="font-display font-bold text-2xl text-white mb-6">{TITLES[step]}</h2>
            <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}>
              <Current form={form} set={set} unavailable={unavailable} pagarmeEnabled={pagarmeEnabled}
                contract={contract} loading={contractLoading} acceptAccess={acceptAccess} setAcceptAccess={setAcceptAccess}
                acceptContract={acceptContract} setAcceptContract={setAcceptContract} />
            </motion.div>
            <div className="mt-10 flex items-center justify-between gap-4">
              <button type="button" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} data-testid="checkout-back-button"
                className="inline-flex items-center gap-2 text-zinc-300 hover:text-white disabled:opacity-0"><ArrowLeft className="w-4 h-4" /> Voltar</button>
              {step < 4 ? (
                <button type="button" onClick={next} data-testid="checkout-next-button"
                  className="inline-flex items-center gap-2 rounded-full bg-[#FFD200] text-[#08080C] font-semibold px-8 py-4 hover:bg-white transition-colors">
                  {step === 3 ? "Revisar contrato" : "Continuar"} <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button type="button" onClick={pay} disabled={!canPay} data-testid="checkout-submit-pay-button"
                  className="inline-flex items-center gap-2 rounded-full bg-[#E8202A] text-white font-semibold px-6 sm:px-8 py-4 hover:bg-[#FFD200] hover:text-[#08080C] transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-[#E8202A] disabled:hover:text-white">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Prosseguir para o pagamento <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </section>
          <div className="lg:col-span-5"><div className="lg:sticky lg:top-28"><Summary form={form} /></div></div>
        </div>
      </div>
    </main>
  );
}
